import { useEffect, useMemo, useRef, useState } from "react";
import { styled, keyframes } from "@pigment-css/react";
import { baseTheme, type GlobalTokens } from "@acto/web-common/theme";
import { ActoLogo } from "@acto/web-common/components";
import { useFlipResize } from "@acto/taskpane/field-sales-2/utils/hooks/useFlipResize";

const DEFAULT_INTERVAL_MS = 1600;

// Width morph and text crossfade share one duration/easing so a label change resolves as a single
// coordinated motion: the box and the text finish together.
const MORPH_MS = parseFloat(baseTheme.fieldSalesTwo.motion.duration.slow);
const MORPH_EASING = baseTheme.fieldSalesTwo.motion.easing.emphasized;

interface CyclingLoaderPillProps {
  /** Opening frame, shown once before the stages cycle (e.g. the customer name). Omit to start on the stages. */
  leadText?: string;
  /** Cycling frames shown after the lead; loops back to the first stage after the last. */
  stages: string[];
  /** While true the text cycles; false resets to the opening frame. */
  active: boolean;
  /** Milliseconds between frames. Defaults to the recap loader's cadence. */
  intervalMs?: number;
}

// The two-card stack: front title pill over a narrower decorative pill peeking below.
const Stack = styled("div")({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  maxWidth: "100%",
});

const sharedPill = (theme: GlobalTokens) => ({
  height: theme.fieldSalesTwo.spacing[48],
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
});

// Decorative empty capsule peeking ~8px below the front pill, behind it.
const BackPill = styled("div")(({ theme }) => ({
  ...sharedPill(theme),
  position: "absolute",
  top: theme.fieldSalesTwo.spacing[8],
  width: 200,
  maxWidth: "100%",
  zIndex: 0,
}));

// Brand icon + the cycling text. Its width is content-driven; the FLIP resize animates it
// between label lengths (plain CSS can't transition an auto width).
const FrontPill = styled("div")(({ theme }) => ({
  ...sharedPill(theme),
  position: "relative",
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  maxWidth: "100%",
  minWidth: 0,
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

// Holds the crossfading text layers. The incoming label stays in normal flow so it
// drives the pill's natural width (which the FLIP measures); the outgoing label is
// taken out of flow so it fades out without affecting that width.
const TextArea = styled("span")({
  position: "relative",
  display: "flex",
  alignItems: "center",
  minWidth: 0,
});

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const fadeOut = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
});

const textBase = (theme: GlobalTokens) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[800],
});

// Incoming label — in flow (drives width), re-mounted per frame (keyed) so it fades in.
const PillTextIn = styled("span")(({ theme }) => ({
  ...textBase(theme),
  minWidth: 0,
  animation: `${fadeIn} ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.emphasized} both`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}));

// Outgoing label — pinned centered out of flow, fading out over the same window.
const PillTextOut = styled("span")(({ theme }) => ({
  ...textBase(theme),
  position: "absolute",
  top: 0,
  bottom: 0,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  pointerEvents: "none",
  animation: `${fadeOut} ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.emphasized} forwards`,
  // Reduced motion skips the crossfade: drop the outgoing label at once instead of overlapping it.
  "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0 },
}));

/**
 * A branded loading pill whose text crossfades through a sequence while `active`. The optional
 * `leadText` opens the sequence once, then the `stages` loop indefinitely, so the pill outlasts a
 * slow operation and is simply unmounted by the parent when a fast one finishes early. The pill
 * width and the label crossfade share one duration/easing so a label change resolves as one motion.
 *
 * Layout, error handling, and the done/close transition belong to each consumer; this component
 * owns only the pill visual and the text cycling.
 */
export function CyclingLoaderPill({
  leadText,
  stages,
  active,
  intervalMs = DEFAULT_INTERVAL_MS,
}: CyclingLoaderPillProps) {
  // Frame 0 is the lead (shown once); the rest are the cycling stages.
  const frames = useMemo(() => (leadText ? [leadText, ...stages] : stages), [leadText, stages]);
  // Where the loop restarts after the last frame — past the lead, so the name only opens the sequence.
  const loopStart = leadText ? 1 : 0;

  const [frameIndex, setFrameIndex] = useState(0);
  useEffect(() => {
    if (!active) {
      setFrameIndex(0);
      return;
    }
    const id = setInterval(() => {
      setFrameIndex((i) => (i + 1 >= frames.length ? loopStart : i + 1));
    }, intervalMs);
    return () => clearInterval(id);
  }, [active, frames.length, loopStart, intervalMs]);

  // Track the frame leaving the screen so it can fade out alongside the incoming one.
  const [outgoingIndex, setOutgoingIndex] = useState<number | null>(null);
  const lastFrameRef = useRef(frameIndex);
  useEffect(() => {
    const prev = lastFrameRef.current;
    lastFrameRef.current = frameIndex;
    if (prev === frameIndex) return;
    setOutgoingIndex(prev);
    const timer = window.setTimeout(() => setOutgoingIndex(null), MORPH_MS);
    return () => window.clearTimeout(timer);
  }, [frameIndex]);

  // Ease the pill's content-driven width between differing label lengths instead of snapping.
  const pillRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  useFlipResize({
    elementRef: pillRef,
    contentRef,
    deps: [frameIndex],
    durationMs: MORPH_MS,
    easing: MORPH_EASING,
    axes: "width",
  });

  return (
    <Stack>
      <BackPill aria-hidden="true" />
      <FrontPill ref={pillRef}>
        <ActoLogo width={15} height={20} aria-hidden="true" />
        <TextArea ref={contentRef}>
          <PillTextIn key={frameIndex}>{frames[frameIndex]}</PillTextIn>
          {outgoingIndex != null && <PillTextOut key={`out-${outgoingIndex}`}>{frames[outgoingIndex]}</PillTextOut>}
        </TextArea>
      </FrontPill>
    </Stack>
  );
}
